import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Crosshair, AlertTriangle, ShieldAlert, Activity, Sparkles, Clock, MapPin, CheckCircle2, ChevronRight, Twitter, Facebook, Instagram, Globe, MessageSquare, Users, Network, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { NationalThreatLevel } from '../components/dashboard/NationalThreatLevel';
import { PresidentialStabilityIndex } from '../components/dashboard/PresidentialStabilityIndex';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/Dialog';
const moodData = [{
  name: 'Fear',
  value: 65,
  color: 'bg-orange-500'
}, {
  name: 'Anger',
  value: 72,
  color: 'bg-red-500'
}, {
  name: 'Distrust',
  value: 58,
  color: 'bg-amber-500'
}, {
  name: 'Optimism',
  value: 35,
  color: 'bg-emerald-500'
}, {
  name: 'Frustration',
  value: 68,
  color: 'bg-orange-500'
}, {
  name: 'Nationalism',
  value: 82,
  color: 'bg-primary'
}];
const liveNarratives = [{
  id: 1,
  time: 'Baru saja',
  platform: Twitter,
  text: 'Mobilisasi tagar #TolakKebijakanX menembus 50k tweets dalam 1 jam.',
  sentiment: 'Negatif',
  reach: '2.4M',
  risk: 88,
  isNew: true
}, {
  id: 2,
  time: '12 mnt lalu',
  platform: Facebook,
  text: 'Video deepfake audio Presiden dibagikan di 45 grup komunitas lokal.',
  sentiment: 'Sangat Negatif',
  reach: '850K',
  risk: 95,
  isNew: false
}, {
  id: 3,
  time: '28 mnt lalu',
  platform: Instagram,
  text: 'Influencer oposisi mengamplifikasi isu kelangkaan pupuk di Jateng.',
  sentiment: 'Negatif',
  reach: '1.2M',
  risk: 72,
  isNew: false
}, {
  id: 4,
  time: '45 mnt lalu',
  platform: Globe,
  text: 'Media asing menerbitkan artikel spekulasi stabilitas kabinet.',
  sentiment: 'Netral',
  reach: '400K',
  risk: 65,
  isNew: false
}, {
  id: 5,
  time: '1 jam lalu',
  platform: Twitter,
  text: 'Sentimen positif muncul dari peresmian bendungan di Jatim.',
  sentiment: 'Positif',
  reach: '1.8M',
  risk: 12,
  isNew: false
}, {
  id: 6,
  time: '2 jam lalu',
  platform: MessageSquare,
  text: 'Pesan berantai WhatsApp hoaks pencabutan bansos menyebar.',
  sentiment: 'Negatif',
  reach: 'Unknown',
  risk: 84,
  isNew: false
}];
const activeThreats = [{
  id: 1,
  type: 'Deepfake Audio',
  target: 'Presiden RI',
  status: 'ACTIVE',
  time: '1j 15m',
  severity: 'critical',
  color: 'border-red-500',
  bg: 'bg-red-500/10',
  text: 'text-red-500'
}, {
  id: 2,
  type: 'Bot Farm (1.2k)',
  target: 'Kebijakan Pangan',
  status: 'INVESTIGATING',
  time: '3j 45m',
  severity: 'warning',
  color: 'border-amber-500',
  bg: 'bg-amber-500/10',
  text: 'text-amber-500'
}, {
  id: 3,
  type: 'Foreign Influence',
  target: 'Stabilitas Kabinet',
  status: 'ACTIVE',
  time: '5j 20m',
  severity: 'warning',
  color: 'border-amber-500',
  bg: 'bg-amber-500/10',
  text: 'text-amber-500'
}, {
  id: 4,
  type: 'Hostile Narrative',
  target: 'Kunjungan Papua',
  status: 'CONTAINED',
  time: '12j 10m',
  severity: 'info',
  color: 'border-emerald-500',
  bg: 'bg-emerald-500/10',
  text: 'text-emerald-500'
}];
const actorMovement = [{
  id: 1,
  initial: 'AX',
  name: '@aktor_x',
  action: 'Mempublikasikan 14 unggahan dalam 6 jam — koneksi dengan 3 akun terdeteksi',
  score: 92,
  trend: 'up'
}, {
  id: 2,
  initial: 'OP',
  name: 'Oposisi_Pusat',
  action: 'Mengubah narasi dari isu ekonomi ke isu keamanan nasional',
  score: 85,
  trend: 'up'
}, {
  id: 3,
  initial: 'SN',
  name: 'Sindikat_News',
  action: 'Menyebarkan link artikel framing negatif ke 20+ grup publik',
  score: 78,
  trend: 'stable'
}, {
  id: 4,
  initial: 'BF',
  name: 'Bot_Farm_04',
  action: 'Aktivitas menurun setelah platform melakukan takedown massal',
  score: 45,
  trend: 'down'
}];
const ministryStatus = [{
  id: 1,
  name: 'KOMDIGI',
  issue: 'Deepfake Audio Presiden',
  status: 'PROSES',
  time: '1j 10m',
  score: 85
}, {
  id: 2,
  name: 'KEMENTAN',
  issue: 'Kelangkaan Pupuk',
  status: 'BELUM RESPONS',
  time: '4j 30m',
  score: 42
}, {
  id: 3,
  name: 'POLRI',
  issue: 'Ancaman Kunjungan Papua',
  status: 'TUNTAS',
  time: '-',
  score: 95
}, {
  id: 4,
  name: 'KEMENSETNEG',
  issue: 'Spekulasi Kabinet',
  status: 'KLARIFIKASI',
  time: '2j 15m',
  score: 78
}, {
  id: 5,
  name: 'KEMENSOS',
  issue: 'Hoaks Bansos',
  status: 'BELUM RESPONS',
  time: '3j 45m',
  score: 35
}];
const agenda = [{
  id: 1,
  title: 'Rapat Terbatas Ekonomi',
  loc: 'Istana Negara',
  time: '09:00',
  risk: 'LOW',
  status: 'done'
}, {
  id: 2,
  title: 'Menerima Tamu Negara',
  loc: 'Istana Merdeka',
  time: '11:30',
  risk: 'LOW',
  status: 'done'
}, {
  id: 3,
  title: 'Sidang Kabinet Paripurna',
  loc: 'Istana Negara',
  time: '14:00',
  risk: 'MEDIUM',
  status: 'active'
}, {
  id: 4,
  title: 'Kunjungan Kerja',
  loc: 'Papua (Jayapura)',
  time: '16:30',
  risk: 'HIGH',
  status: 'upcoming'
}];
const escalationSteps = [{
  id: 1,
  label: 'DETEKSI',
  status: 'done'
}, {
  id: 2,
  label: 'ANALISIS',
  status: 'done'
}, {
  id: 3,
  label: 'VERIFIKASI',
  status: 'done'
}, {
  id: 4,
  label: 'ESKALASI',
  status: 'active'
}, {
  id: 5,
  label: 'KEPUTUSAN PRESIDEN',
  status: 'pending'
}, {
  id: 6,
  label: 'EKSEKUSI',
  status: 'pending'
}];
export function WarRoom() {
  const [feedFilter, setFeedFilter] = useState<'all' | 'critical' | 'negative'>('all');
  const [selectedNarrative, setSelectedNarrative] = useState<any>(null);
  const [selectedActor, setSelectedActor] = useState<any>(null);
  const [selectedMinistry, setSelectedMinistry] = useState<any>(null);
  const filteredNarratives = liveNarratives.filter(item => {
    if (feedFilter === 'critical') return item.risk >= 80;
    if (feedFilter === 'negative') return item.sentiment.includes('Negatif');
    return true;
  });
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6 pb-10" data-id="element-3533">
      <PageHeader group="Intelijen Presiden" title="Garuda Shield War Room" description="National Digital Operations Center · Live monitoring stabilitas nasional dan ancaman terhadap Presiden RI." icon={Crosshair} data-id="element-3534" />

      {/* ROW 1: Command Strip */}
      <div className="grid grid-cols-12 gap-4 sm:gap-6" data-id="element-3535">
        <div className="col-span-12 lg:col-span-8" data-id="element-3536">
          <NationalThreatLevel currentLevel="orange" data-id="element-3537" />
        </div>
        <div className="col-span-12 lg:col-span-4" data-id="element-3538">
          <Card className="bg-card border-red-500/50 h-full flex flex-col threat-pulse-red relative overflow-hidden" data-id="element-3539">
            <div className="absolute inset-0 bg-red-500/5 pointer-events-none" data-id="element-3540" />
            <CardHeader className="pb-2 pt-5 px-5" data-id="element-3541">
              <CardTitle className="font-mono text-sm uppercase tracking-wider text-red-500 flex items-center gap-2" data-id="element-3542">
                <AlertTriangle className="size-5" data-id="element-3543" />
                Crisis Mode Status
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 flex-1 flex flex-col justify-between" data-id="element-3544">
              <div data-id="element-3545">
                <h2 className="text-2xl font-black text-foreground uppercase tracking-tight mb-1" data-id="element-3546">
                  Krisis Digital Aktif
                </h2>
                <div className="flex items-center gap-2 mb-4" data-id="element-3547">
                  <Badge className="bg-orange-500 hover:bg-orange-600 text-white font-mono text-[10px] uppercase" data-id="element-3548">
                    Level: ORANGE
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground" data-id="element-3549">
                    T-Plus: 04:22:15
                  </span>
                </div>
                <div className="space-y-2 font-mono text-xs text-muted-foreground" data-id="element-3550">
                  <div className="flex justify-between" data-id="element-3551">
                    <span data-id="element-3552">Mulai:</span>
                    <span className="font-mono text-foreground" data-id="element-3553">
                      22 Mei 2026 · 10:10 WIB
                    </span>
                    <span className="text-foreground" data-id="element-3554">
                      03 Mei 2026 · 10:10 WIB
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3555">
                    <span data-id="element-3556">Durasi:</span>
                    <span className="text-foreground" data-id="element-3557">4 Jam 22 Menit</span>
                  </div>
                  <div className="flex justify-between" data-id="element-3558">
                    <span data-id="element-3559">Protokol:</span>
                    <span className="text-foreground" data-id="element-3560">
                      Alpha-1 (Monitoring Ketat)
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-6" data-id="element-3561">
                <Button variant="outline" className="border-red-500/50 text-red-500 hover:bg-red-500/10 font-mono text-[10px] h-9" onClick={() => toast.error('Eskalasi ke RED dikirim ke Komandan', {
                description: 'Protokol Bravo-2 disiapkan. Konfirmasi diperlukan dalam 5 menit.'
              })} data-id="element-3562">
                  Eskalasi ke RED
                </Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white font-mono text-[10px] h-9" onClick={() => toast.success('Protokol B Aktif', {
                description: 'Tim Krisis Digital diperkuat. Semua K/L diaktifkan stand-by.'
              })} data-id="element-3563">
                  Aktifkan Protokol B
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ROW 2: Stability + Mood */}
      <div className="grid grid-cols-12 gap-4 sm:gap-6" data-id="element-3564">
        <div className="col-span-12 lg:col-span-7" data-id="element-3565">
          <PresidentialStabilityIndex data-id="element-3566" />
        </div>
        <div className="col-span-12 lg:col-span-5" data-id="element-3567">
          <Card className="bg-card border-border/50 h-full flex flex-col min-h-[400px]" data-id="element-3568">
            <CardHeader className="pb-4 pt-5 px-5 border-b border-border/50 bg-muted/10" data-id="element-3569">
              <CardTitle className="font-mono text-sm uppercase tracking-wider text-foreground flex items-center gap-2" data-id="element-3570">
                <Activity className="size-4 text-orange-500" data-id="element-3571" />
                Temperatur Psikologis Nasional
              </CardTitle>
              <div className="mt-3" data-id="element-3572">
                <span className="text-3xl font-black text-orange-500 uppercase tracking-tight" data-id="element-3573">
                  Gelisah
                </span>
                <p className="text-xs text-muted-foreground mt-1" data-id="element-3574">
                  Dominasi emosi negatif terkait isu ekonomi dan keamanan.
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-5 flex-1 flex flex-col justify-center gap-4" data-id="element-3575">
              {moodData.map(mood => <div key={mood.name} className="flex flex-col gap-1.5" data-id="element-3576">
                  <div className="flex items-center justify-between" data-id="element-3577">
                    <span className="text-xs font-medium text-foreground uppercase tracking-wide" data-id="element-3578">
                      {mood.name}
                    </span>
                    <span className="font-mono text-xs font-bold text-foreground" data-id="element-3579">
                      {mood.value}%
                    </span>
                  </div>
                  <div className="h-2.5 w-full bg-muted rounded-full overflow-hidden" data-id="element-3580">
                    <div className={`h-full rounded-full ${mood.color}`} style={{
                  width: `${mood.value}%`
                }} data-id="element-3581" />
                  </div>
                </div>)}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ROW 3: Live Operations Grid */}
      <div className="grid grid-cols-12 gap-4 sm:gap-6" data-id="element-3582">
        {/* Live Narrative Feed */}
        <div className="col-span-12 lg:col-span-6" data-id="element-3583">
          <Card className="bg-card border-border/50 h-[450px] flex flex-col" data-id="element-3584">
            <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50" data-id="element-3585">
              <CardTitle className="font-mono text-xs uppercase tracking-wider flex items-center justify-between" data-id="element-3586">
                <div className="flex items-center gap-2" data-id="element-3587">
                  <MessageSquare className="size-4 text-primary" data-id="element-3588" />
                  Live Narrative Feed
                </div>
                <Badge variant="outline" className="font-mono text-[9px] h-5" data-id="element-3589">
                  {filteredNarratives.length} ITEM
                </Badge>
              </CardTitle>
              <div className="flex items-center gap-1.5 mt-3" data-id="element-3590">
                {[{
                id: 'all',
                label: 'Semua'
              }, {
                id: 'critical',
                label: 'Kritis'
              }, {
                id: 'negative',
                label: 'Negatif'
              }].map(f => <button key={f.id} onClick={() => setFeedFilter(f.id as any)} className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition ${feedFilter === f.id ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-muted/30 border-border/50 text-muted-foreground hover:bg-muted/50'}`} data-id="element-3591">
                    {f.label}
                  </button>)}
              </div>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent" data-id="element-3592">
              {filteredNarratives.length > 0 ? <div className="flex flex-col divide-y divide-border/50" data-id="element-3593">
                  {filteredNarratives.map(item => <button key={item.id} onClick={() => setSelectedNarrative(item)} className={`p-4 flex gap-3 ${item.isNew ? 'bg-primary/5 relative' : 'hover:bg-muted/30'}`} data-id="element-3594">
                      {item.isNew && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary animate-pulse" data-id="element-3595" />}
                      <div className="mt-0.5 shrink-0" data-id="element-3596">
                        <item.platform className="size-4 text-muted-foreground" data-id="element-3597" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-3598">
                        <div className="flex items-center justify-between mb-1" data-id="element-3599">
                          <span className="font-mono text-[9px] text-muted-foreground" data-id="element-3600">
                            {item.time}
                          </span>
                          <span className={`font-mono text-[9px] font-bold ${item.risk > 80 ? 'text-red-500' : item.risk > 60 ? 'text-amber-500' : 'text-emerald-500'}`} data-id="element-3601">
                            RISK: {item.risk}
                          </span>
                        </div>
                        <p className="text-xs font-medium text-foreground leading-snug mb-2" data-id="element-3602">
                          {item.text}
                        </p>
                        <div className="flex items-center gap-2" data-id="element-3603">
                          <Badge variant="outline" className="font-mono text-[8px] h-4 px-1.5 border-border/50" data-id="element-3604">
                            {item.sentiment}
                          </Badge>
                          <span className="font-mono text-[9px] text-muted-foreground" data-id="element-3605">
                            Reach: {item.reach}
                          </span>
                        </div>
                      </div>
                    </button>)}
                </div> : <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-6" data-id="element-3606">
                  <MessageSquare className="size-8 mb-2 opacity-50" data-id="element-3607" />
                  <p className="text-xs" data-id="element-3608">Tidak ada narasi sesuai filter</p>
                </div>}
            </CardContent>
          </Card>
        </div>

        {/* Active Threats */}
        <div className="col-span-12 lg:col-span-6" data-id="element-3609">
          <Card className="bg-card border-border/50 h-[450px] flex flex-col" data-id="element-3610">
            <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50" data-id="element-3611">
              <CardTitle className="font-mono text-xs uppercase tracking-wider flex items-center justify-between" data-id="element-3612">
                <div className="flex items-center gap-2" data-id="element-3613">
                  <ShieldAlert className="size-4 text-red-500" data-id="element-3614" />
                  Active Threats
                </div>
                <Badge variant="outline" className="font-mono text-[9px] h-5 border-red-500/30 text-red-500 bg-red-500/10" data-id="element-3615">
                  4 AKTIF
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 flex-1 overflow-y-auto flex flex-col gap-3" data-id="element-3616">
              {activeThreats.map(threat => <div key={threat.id} className={`border-l-2 ${threat.color} bg-muted/20 rounded-r-md p-3 flex flex-col gap-2`} data-id="element-3617">
                  <div className="flex items-center justify-between" data-id="element-3618">
                    <div className="flex items-center gap-2" data-id="element-3619">
                      <Badge variant="outline" className={`font-mono text-[9px] h-4 px-1.5 border-none ${threat.bg} ${threat.text}`} data-id="element-3620">
                        {threat.status}
                      </Badge>
                      <span className="font-mono text-[9px] text-muted-foreground flex items-center gap-1" data-id="element-3621">
                        <Clock className="size-3" data-id="element-3622" /> {threat.time}
                      </span>
                    </div>
                  </div>
                  <div data-id="element-3623">
                    <p className="text-xs font-bold text-foreground" data-id="element-3624">
                      {threat.type}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5" data-id="element-3625">
                      Target:{' '}
                      <span className="text-foreground font-medium" data-id="element-3626">
                        {threat.target}
                      </span>
                    </p>
                  </div>
                </div>)}
            </CardContent>
          </Card>
        </div>

        {/* Actor Movement */}
        <div className="col-span-12 lg:col-span-6" data-id="element-3627">
          <Card className="bg-card border-border/50 h-[400px] flex flex-col" data-id="element-3628">
            <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50" data-id="element-3629">
              <CardTitle className="font-mono text-xs uppercase tracking-wider flex items-center gap-2" data-id="element-3630">
                <Network className="size-4 text-primary" data-id="element-3631" />
                Actor Movement
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-y-auto" data-id="element-3632">
              <div className="flex flex-col divide-y divide-border/50" data-id="element-3633">
                {actorMovement.map(actor => <button key={actor.id} onClick={() => setSelectedActor(actor)} className="p-4 flex items-start gap-3 hover:bg-muted/30 text-left w-full" data-id="element-3634">
                    <Avatar className="size-8 border border-border/50" data-id="element-3635">
                      <AvatarFallback className="bg-muted text-[10px] font-bold" data-id="element-3636">
                        {actor.initial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0" data-id="element-3637">
                      <div className="flex items-center justify-between mb-1" data-id="element-3638">
                        <span className="text-xs font-bold text-foreground" data-id="element-3639">
                          {actor.name}
                        </span>
                        <div className="flex items-center gap-1" data-id="element-3640">
                          <span className="font-mono text-[10px] font-bold text-primary" data-id="element-3641">
                            INF: {actor.score}
                          </span>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground leading-snug" data-id="element-3642">
                        {actor.action}
                      </p>
                    </div>
                  </button>)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Ministry Response Status */}
        <div className="col-span-12 lg:col-span-6" data-id="element-3643">
          <Card className="bg-card border-border/50 h-[400px] flex flex-col" data-id="element-3644">
            <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50" data-id="element-3645">
              <CardTitle className="font-mono text-xs uppercase tracking-wider flex items-center gap-2" data-id="element-3646">
                <Building2 className="size-4 text-primary" data-id="element-3647" />
                Ministry Response Status
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-y-auto" data-id="element-3648">
              <table className="w-full text-left border-collapse" data-id="element-3649">
                <thead className="bg-muted/20 border-b border-border/50" data-id="element-3650">
                  <tr data-id="element-3651">
                    <th className="font-mono text-[9px] font-medium text-muted-foreground p-3" data-id="element-3652">
                      K/L
                    </th>
                    <th className="font-mono text-[9px] font-medium text-muted-foreground p-3" data-id="element-3653">
                      ISU
                    </th>
                    <th className="font-mono text-[9px] font-medium text-muted-foreground p-3" data-id="element-3654">
                      STATUS
                    </th>
                    <th className="font-mono text-[9px] font-medium text-muted-foreground p-3 text-right" data-id="element-3655">
                      SCORE
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50" data-id="element-3656">
                  {ministryStatus.map(min => <tr key={min.id} onClick={() => setSelectedMinistry(min)} className="hover:bg-muted/30 cursor-pointer" data-id="element-3657">
                      <td className="p-3 text-[10px] font-bold text-foreground" data-id="element-3658">
                        {min.name}
                      </td>
                      <td className="p-3 text-[10px] text-muted-foreground truncate max-w-[120px]" data-id="element-3659">
                        {min.issue}
                      </td>
                      <td className="p-3" data-id="element-3660">
                        <Badge variant="outline" className={`font-mono text-[8px] h-4 px-1.5 border-none ${min.status === 'TUNTAS' ? 'bg-emerald-500/10 text-emerald-500' : min.status === 'PROSES' || min.status === 'KLARIFIKASI' ? 'bg-amber-500/10 text-amber-500' : 'bg-red-500/10 text-red-500'}`} data-id="element-3661">
                          {min.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-right" data-id="element-3662">
                        <span className={`font-mono text-[10px] font-bold ${min.score >= 80 ? 'text-emerald-500' : min.score >= 60 ? 'text-amber-500' : 'text-red-500'}`} data-id="element-3663">
                          {min.score}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ROW 4: Presidential Agenda + Escalation Path */}
      <div className="grid grid-cols-12 gap-4 sm:gap-6" data-id="element-3664">
        {/* Presidential Agenda */}
        <div className="col-span-12 lg:col-span-5" data-id="element-3665">
          <Card className="bg-card border-border/50 h-full" data-id="element-3666">
            <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50" data-id="element-3667">
              <CardTitle className="font-mono text-xs uppercase tracking-wider flex items-center gap-2" data-id="element-3668">
                <MapPin className="size-4 text-primary" data-id="element-3669" />
                Presidential Agenda Today
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5" data-id="element-3670">
              <div className="relative border-l border-border/50 ml-2 space-y-6" data-id="element-3671">
                {agenda.map(item => <div key={item.id} className="relative pl-6" data-id="element-3672">
                    <div className={`absolute -left-1.5 top-1 size-3 rounded-full border-2 border-background ${item.status === 'done' ? 'bg-muted-foreground' : item.status === 'active' ? 'bg-primary ring-4 ring-primary/20' : 'bg-muted border-muted-foreground'}`} data-id="element-3673" />
                    <div className={`p-3 rounded-md border ${item.status === 'active' ? 'border-primary bg-primary/5' : 'border-border/50 bg-muted/10'}`} data-id="element-3674">
                      <div className="flex items-center justify-between mb-1" data-id="element-3675">
                        <span className="font-mono text-[10px] text-muted-foreground" data-id="element-3676">
                          {item.time}
                        </span>
                        <Badge variant="outline" className={`font-mono text-[8px] h-4 px-1.5 border-none ${item.risk === 'HIGH' ? 'bg-red-500/10 text-red-500' : item.risk === 'MEDIUM' ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}`} data-id="element-3677">
                          RISK: {item.risk}
                        </Badge>
                      </div>
                      <p className="text-xs font-bold text-foreground" data-id="element-3678">
                        {item.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5" data-id="element-3679">
                        {item.loc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Escalation Path */}
        <div className="col-span-12 lg:col-span-7" data-id="element-3680">
          <Card className="bg-card border-border/50 h-full flex flex-col" data-id="element-3681">
            <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50" data-id="element-3682">
              <CardTitle className="font-mono text-xs uppercase tracking-wider flex items-center gap-2" data-id="element-3683">
                <Network className="size-4 text-primary" data-id="element-3684" />
                Crisis Escalation Path
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 flex-1 flex flex-col" data-id="element-3685">
              {/* Stepper */}
              <div className="flex items-center justify-between mb-8 relative" data-id="element-3686">
                <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-border/50 -translate-y-1/2 z-0" data-id="element-3687" />
                {escalationSteps.map((step, idx) => <div key={step.id} className="relative z-10 flex flex-col items-center gap-2 bg-card px-2" data-id="element-3688">
                    <div className={`size-6 rounded-full flex items-center justify-center border-2 ${step.status === 'done' ? 'bg-primary border-primary text-primary-foreground' : step.status === 'active' ? 'bg-background border-primary text-primary ring-4 ring-primary/20' : 'bg-background border-border text-muted-foreground'}`} data-id="element-3689">
                      {step.status === 'done' ? <CheckCircle2 className="size-3.5" data-id="element-3690" /> : <span className="font-mono text-[10px]" data-id="element-3691">{step.id}</span>}
                    </div>
                    <span className={`font-mono text-[9px] text-center max-w-[60px] ${step.status === 'active' ? 'text-primary font-bold' : 'text-muted-foreground'}`} data-id="element-3692">
                      {step.label}
                    </span>
                  </div>)}
              </div>

              {/* Decision Support AI */}
              <div className="mt-auto bg-primary/5 border border-primary/20 rounded-md p-4" data-id="element-3693">
                <div className="flex items-center gap-2 mb-3" data-id="element-3694">
                  <Sparkles className="size-4 text-primary" data-id="element-3695" />
                  <h4 className="font-mono text-[10px] font-bold text-primary uppercase tracking-wider" data-id="element-3696">
                    Decision Support AI
                  </h4>
                </div>
                <ul className="space-y-2.5" data-id="element-3697">
                  <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-3698">
                    <Sparkles className="size-3 text-primary shrink-0 mt-0.5" data-id="element-3699" />
                    <span data-id="element-3700">
                      Presiden tidak perlu merespons langsung — gunakan Kominfo
                      sebagai juru bicara teknis.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-3701">
                    <Sparkles className="size-3 text-primary shrink-0 mt-0.5" data-id="element-3702" />
                    <span data-id="element-3703">
                      Klarifikasi maksimal 4 jam untuk mencegah eskalasi ke
                      tingkat RED.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-3704">
                    <Sparkles className="size-3 text-primary shrink-0 mt-0.5" data-id="element-3705" />
                    <span data-id="element-3706">
                      Influencer lokal lebih efektif dibanding statement resmi{' '}
                      <span className="text-primary font-mono text-[10px] font-bold ml-1" data-id="element-3707">
                        (CONFIDENCE 87%)
                      </span>
                      .
                    </span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Narrative Detail Dialog */}
      <Dialog open={!!selectedNarrative} onOpenChange={o => !o && setSelectedNarrative(null)} data-id="element-3708">
        <DialogContent className="max-w-lg" data-id="element-3709">
          <DialogHeader data-id="element-3710">
            <div className="flex items-center gap-2 mb-1" data-id="element-3711">
              <Badge variant="outline" className="font-mono text-[9px] h-4" data-id="element-3712">
                {selectedNarrative?.sentiment}
              </Badge>
              <span className={`font-mono text-[9px] font-bold ${selectedNarrative?.risk > 80 ? 'text-red-500' : selectedNarrative?.risk > 60 ? 'text-amber-500' : 'text-emerald-500'}`} data-id="element-3713">
                RISK: {selectedNarrative?.risk}
              </span>
              <span className="font-mono text-[9px] text-muted-foreground ml-auto" data-id="element-3714">
                {selectedNarrative?.time}
              </span>
            </div>
            <DialogTitle data-id="element-3715">Detail Narasi</DialogTitle>
            <DialogDescription data-id="element-3716">
              Reach estimasi: {selectedNarrative?.reach} · Eskalasi otomatis
              tersedia bila RISK ≥ 80.
            </DialogDescription>
          </DialogHeader>
          {selectedNarrative && <div className="space-y-3" data-id="element-3717">
              <div className="p-3 rounded-lg border border-border/50 bg-muted/20" data-id="element-3718">
                <p className="text-sm leading-relaxed" data-id="element-3719">
                  {selectedNarrative.text}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center" data-id="element-3720">
                <div className="p-2 rounded bg-muted/10 border border-border/50" data-id="element-3721">
                  <p className="font-mono text-[9px] text-muted-foreground uppercase" data-id="element-3722">
                    Risk Score
                  </p>
                  <p className="font-bold text-lg mt-0.5" data-id="element-3723">
                    {selectedNarrative.risk}
                  </p>
                </div>
                <div className="p-2 rounded bg-muted/10 border border-border/50" data-id="element-3724">
                  <p className="font-mono text-[9px] text-muted-foreground uppercase" data-id="element-3725">
                    Reach
                  </p>
                  <p className="font-bold text-lg mt-0.5" data-id="element-3726">
                    {selectedNarrative.reach}
                  </p>
                </div>
                <div className="p-2 rounded bg-muted/10 border border-border/50" data-id="element-3727">
                  <p className="font-mono text-[9px] text-muted-foreground uppercase" data-id="element-3728">
                    Sentimen
                  </p>
                  <p className="font-bold text-xs mt-1" data-id="element-3729">
                    {selectedNarrative.sentiment}
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3" data-id="element-3730">
                <p className="font-mono text-[10px] uppercase text-primary font-bold flex items-center gap-1.5" data-id="element-3731">
                  <Sparkles className="size-3" data-id="element-3732" /> Tindakan Disarankan
                </p>
                <ul className="text-xs mt-1.5 space-y-1 text-foreground/80" data-id="element-3733">
                  <li data-id="element-3734">· Counter Narrative via KSP/Komdigi (4 jam)</li>
                  <li data-id="element-3735">· Submit takedown ke platform</li>
                  <li data-id="element-3736">· Aktifkan monitoring eskalasi 24 jam</li>
                </ul>
              </div>
            </div>}
          <DialogFooter data-id="element-3737">
            <Button variant="outline" onClick={() => setSelectedNarrative(null)} data-id="element-3738">
              Tutup
            </Button>
            <Button onClick={() => {
            toast.success('Counter narrative diteruskan ke Komdigi');
            setSelectedNarrative(null);
          }} data-id="element-3739">
              Eskalasi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actor Detail Dialog */}
      <Dialog open={!!selectedActor} onOpenChange={o => !o && setSelectedActor(null)} data-id="element-3740">
        <DialogContent className="max-w-md" data-id="element-3741">
          <DialogHeader data-id="element-3742">
            <div className="flex items-center gap-3 mb-2" data-id="element-3743">
              <Avatar className="size-10 border border-border/50" data-id="element-3744">
                <AvatarFallback className="bg-muted text-xs font-bold" data-id="element-3745">
                  {selectedActor?.initial}
                </AvatarFallback>
              </Avatar>
              <div data-id="element-3746">
                <DialogTitle data-id="element-3747">{selectedActor?.name}</DialogTitle>
                <DialogDescription className="font-mono text-[10px]" data-id="element-3748">
                  Influence Score: {selectedActor?.score}/100
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          {selectedActor && <div className="space-y-3" data-id="element-3749">
              <div className="p-3 rounded-lg border border-border/50 bg-muted/20" data-id="element-3750">
                <p className="font-mono text-[10px] text-muted-foreground uppercase mb-1" data-id="element-3751">
                  Aktivitas Terbaru
                </p>
                <p className="text-sm" data-id="element-3752">{selectedActor.action}</p>
              </div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3" data-id="element-3753">
                <p className="font-mono text-[10px] uppercase text-primary font-bold flex items-center gap-1.5" data-id="element-3754">
                  <Sparkles className="size-3" data-id="element-3755" /> Tindakan Disarankan
                </p>
                <ul className="text-xs mt-1.5 space-y-1 text-foreground/80" data-id="element-3756">
                  <li data-id="element-3757">· Tambahkan ke watchlist intelijen aktor</li>
                  <li data-id="element-3758">· Cek jaringan & klaster terhubung</li>
                  <li data-id="element-3759">· Pantau aktivitas 7 hari ke depan</li>
                </ul>
              </div>
            </div>}
          <DialogFooter data-id="element-3760">
            <Button variant="outline" onClick={() => setSelectedActor(null)} data-id="element-3761">
              Tutup
            </Button>
            <Button onClick={() => {
            toast.success(`${selectedActor?.name} ditambahkan ke watchlist`);
            setSelectedActor(null);
          }} data-id="element-3762">
              Tambah ke Watchlist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ministry Detail Dialog */}
      <Dialog open={!!selectedMinistry} onOpenChange={o => !o && setSelectedMinistry(null)} data-id="element-3763">
        <DialogContent className="max-w-md" data-id="element-3764">
          <DialogHeader data-id="element-3765">
            <DialogTitle className="flex items-center gap-2" data-id="element-3766">
              <Building2 className="size-4 text-primary" data-id="element-3767" />
              {selectedMinistry?.name}
            </DialogTitle>
            <DialogDescription data-id="element-3768">
              Status respons K/L terhadap tasking aktif
            </DialogDescription>
          </DialogHeader>
          {selectedMinistry && <div className="space-y-3" data-id="element-3769">
              <div className="grid grid-cols-2 gap-2" data-id="element-3770">
                <div className="p-2.5 rounded bg-muted/20 border border-border/50" data-id="element-3771">
                  <p className="font-mono text-[10px] text-muted-foreground uppercase" data-id="element-3772">
                    Status
                  </p>
                  <p className="text-sm font-bold mt-0.5" data-id="element-3773">
                    {selectedMinistry.status}
                  </p>
                </div>
                <div className="p-2.5 rounded bg-muted/20 border border-border/50" data-id="element-3774">
                  <p className="font-mono text-[10px] text-muted-foreground uppercase" data-id="element-3775">
                    Skor Respons
                  </p>
                  <p className={`text-sm font-bold mt-0.5 ${selectedMinistry.score >= 80 ? 'text-emerald-500' : selectedMinistry.score >= 60 ? 'text-amber-500' : 'text-red-500'}`} data-id="element-3776">
                    {selectedMinistry.score}/100
                  </p>
                </div>
                <div className="p-2.5 rounded bg-muted/20 border border-border/50 col-span-2" data-id="element-3777">
                  <p className="font-mono text-[10px] text-muted-foreground uppercase" data-id="element-3778">
                    Isu Aktif
                  </p>
                  <p className="text-sm font-medium mt-0.5" data-id="element-3779">
                    {selectedMinistry.issue}
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-id="element-3780">
                <p className="font-mono text-[10px] uppercase text-amber-700 font-bold" data-id="element-3781">
                  Tindakan Disarankan
                </p>
                <ul className="text-xs mt-1.5 space-y-1 text-amber-900" data-id="element-3782">
                  <li data-id="element-3783">· Kirim reminder ke PIC K/L</li>
                  <li data-id="element-3784">· Eskalasi ke KSP bila &lt; 60</li>
                  <li data-id="element-3785">· Cek histori tasking 30 hari</li>
                </ul>
              </div>
            </div>}
          <DialogFooter data-id="element-3786">
            <Button variant="outline" onClick={() => setSelectedMinistry(null)} data-id="element-3787">
              Tutup
            </Button>
            <Button onClick={() => {
            toast.success(`Reminder dikirim ke ${selectedMinistry?.name}`);
            setSelectedMinistry(null);
          }} data-id="element-3788">
              Kirim Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}